import React, { useState } from 'react';
import { authenticatedFetch } from '../../../utils/auth';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';

const SystemManagement: React.FC = () => {
    const [isResetting, setIsResetting] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswordForm, setShowPasswordForm] = useState(false);

    // Reset system for next year
    const handleResetSystem = async () => {
        const confirmation = window.confirm(
            '⚠️ POZOR: Táto akcia nevratne odstráni všetky prihlášky!\n\n' +
            'Zostanú používatelia, ich osobné údaje, administrátori, krúžky a nastavenia systému.\n\n' +
            'Táto akcia je určená na prípravu systému na nový školský rok.\n\n' +
            'Ste si istí, že chcete pokračovať?'
        );

        if (!confirmation) return;

        const doubleConfirmation = window.confirm(
            'Posledné potvrdenie: Naozaj chcete NEVRATNE resetovať celý systém? ' +
            'Všetky používateľské dáta budú stratené!'
        );

        if (!doubleConfirmation) return;

        try {
            setIsResetting(true);
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/reset-system`,
                { method: 'POST' }
            );

            if (response.ok) {
                alert('✅ Systém bol úspešne resetovaný pre nový školský rok!');
            } else {
                const errorData = await response.json();
                alert(`❌ Chyba pri resetovaní systému: ${errorData.error || 'Neznáma chyba'}`);
            }
        } catch (error) {
            console.error('Error resetting system:', error);
            alert('❌ Chyba pri resetovaní systému. Skúste to znova.');
        } finally {
            setIsResetting(false);
        }
    };

    // Change admin password
    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            alert('❌ Nové heslo a potvrdenie hesla sa nezhodujú!');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            alert('❌ Nové heslo musí mať aspoň 6 znakov!');
            return;
        }

        try {
            setIsChangingPassword(true);
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/change-password`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        currentPassword: passwordForm.currentPassword,
                        newPassword: passwordForm.newPassword,
                    }),
                }
            );

            if (response.ok) {
                alert('✅ Heslo bolo úspešne zmenené!');
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setShowPasswordForm(false);
            } else {
                const errorData = await response.json();
                alert(`❌ Chyba pri zmene hesla: ${errorData.error || 'Neznáma chyba'}`);
            }
        } catch (error) {
            console.error('Error changing password:', error);
            alert('❌ Chyba pri zmene hesla. Skúste to znova.');
        } finally {
            setIsChangingPassword(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Admin Password Change Section */}
            <Card>
                <div className="p-6">
                    <h2 className="text-2xl font-semibold text-blue-600 mb-6">
                        🔐 Zmena hesla administrátora
                    </h2>

                    {!showPasswordForm ? (
                        <div className="space-y-4">
                            <p className="text-gray-600">
                                Tu môžete zmeniť heslo pre váš administrátorský účet.
                            </p>
                            <Button
                                variant="primary"
                                onClick={() => setShowPasswordForm(true)}
                            >
                                Zmeniť heslo
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleChangePassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Súčasné heslo
                                </label>
                                <input
                                    type="password"
                                    value={passwordForm.currentPassword}
                                    onChange={(e) => setPasswordForm(prev => ({
                                        ...prev,
                                        currentPassword: e.target.value
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nové heslo (minimálne 6 znakov)
                                </label>
                                <input
                                    type="password"
                                    value={passwordForm.newPassword}
                                    onChange={(e) => setPasswordForm(prev => ({
                                        ...prev,
                                        newPassword: e.target.value
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    minLength={6}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Potvrdiť nové heslo
                                </label>
                                <input
                                    type="password"
                                    value={passwordForm.confirmPassword}
                                    onChange={(e) => setPasswordForm(prev => ({
                                        ...prev,
                                        confirmPassword: e.target.value
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div className="flex space-x-3">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={isChangingPassword}
                                >
                                    {isChangingPassword ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Mením heslo...
                                        </>
                                    ) : (
                                        'Zmeniť heslo'
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        setShowPasswordForm(false);
                                        setPasswordForm({
                                            currentPassword: '',
                                            newPassword: '',
                                            confirmPassword: ''
                                        });
                                    }}
                                >
                                    Zrušiť
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </Card>

            {/* System Reset Section */}
            <Card>
                <div className="p-6">
                    <h2 className="text-2xl font-semibold text-red-600 mb-6">
                        ⚠️ Resetovanie systému pre nový školský rok
                    </h2>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                        <h3 className="font-semibold text-red-800 mb-4 text-lg">
                            Čo sa stane pri resetovaní:
                        </h3>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-medium text-red-700 mb-2">✅ Zostane zachované:</h4>
                                <ul className="text-red-600 text-sm list-disc list-inside space-y-1">
                                    <li>Všetci administrátori</li>
                                    <li>Všetky krúžky a ich nastavenia</li>
                                    <li>Ročníky a ich konfigurácia</li>
                                    <li>Cenové nastavenia systému</li>
                                    <li>Základné nastavenia aplikácie</li>
                                    <li>Všetci používatelia a ich účty</li>
                                    <li>Všetky osobné údaje účastníkov</li>
                                </ul>
                            </div>

                            <div>
                                <h4 className="font-medium text-red-700 mb-2">❌ Bude odstránené:</h4>
                                <ul className="text-red-600 text-sm list-disc list-inside space-y-1">
                                    <li>Všetky prihlášky a ich stavy</li>
                                    <li>Administrátorské záznamy k prihláškam</li>
                                    <li>Všetky prihlasovacie tokeny</li>
                                    <li>Dátumy v nastaveniach (začiatok, koniec)</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start space-x-3">
                            <div className="text-amber-600 text-xl">💡</div>
                            <div>
                                <h4 className="font-medium text-amber-800 mb-1">Odporúčanie:</h4>
                                <p className="text-amber-700 text-sm">
                                    Pred resetovaním systému si stiahnite všetky potrebné reporty a zálohy dát.
                                    Táto akcia je nevratná a je určená na konci školského roku pre prípravu na ďalší rok.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                        <h4 className="font-medium text-gray-800 mb-2">Kroky po resetovaní:</h4>
                        <ol className="text-gray-600 text-sm list-decimal list-inside space-y-1">
                            <li>Nastavte nové dátumy pre prihlasovanie v sekcii "Všeobecné"</li>
                            <li>Skontrolujte a upravte ceny krúžkov ak je potrebné</li>
                            <li>Otestujte systém s testovacou prihláškou</li>
                            <li>Informujte používateľov o otvorení prihlasovania</li>
                        </ol>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            variant="danger"
                            size="lg"
                            onClick={handleResetSystem}
                            disabled={isResetting}
                        >
                            {isResetting ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Resetujem systém...
                                </>
                            ) : (
                                '🔄 Resetovať systém pre nový rok'
                            )}
                        </Button>
                    </div>
                </div>
            </Card>

        </div>
    );
};

export default SystemManagement;
